import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, bookUrl, stripNikud } from "../lib/api";
import { formatHebrewRef } from "../lib/hebrewRef";
import { useAuth } from "../context/AuthContext";
import { Search as SearchIcon, ArrowLeft, BookMarked } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import SaveToNotebookButton from "../components/SaveToNotebookButton";

export default function SearchPage() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "en" ? "ltr" : "rtl";
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const initialQ = params.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [results, setResults] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [groups, setGroups] = useState({ tanakh: 0, commentary: 0, other: 0 });
  const [didYouMean, setDidYouMean] = useState(null);
  const [loading, setLoading] = useState(false);

  const KIND_LABEL = {
    tanakh: t("search.kind_tanakh"),
    commentary: t("search.kind_commentary"),
    other: t("search.kind_other"),
    note: t("search.kind_note"),
    highlight: t("search.kind_highlight"),
    lesson: t("search.kind_lesson"),
  };

  useEffect(() => {
    if (initialQ) runSearch(initialQ);
    // eslint-disable-next-line
  }, []);

  // Re-run search when the auth user becomes available so personal results
  // populate after the AuthContext finishes loading.
  useEffect(() => {
    if (user && initialQ) runSearch(initialQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const runSearch = async (query) => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      // Try personal in parallel — the endpoint requires a token, but
      // catching its rejection lets us still render public results when
      // the user is anonymous.
      const [pub, priv] = await Promise.all([
        api.search(query),
        user ? api.searchPersonal(query).catch(() => null) : Promise.resolve(null),
      ]);
      setResults(pub.results || []);
      setGroups(pub.groups || { tanakh: 0, commentary: 0, other: 0 });
      setDidYouMean(pub.did_you_mean || null);
      setPersonal(priv?.results || []);
    } catch {
      setResults([]);
      setGroups({ tanakh: 0, commentary: 0, other: 0 });
      setDidYouMean(null);
      setPersonal([]);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    setParams({ q });
    runSearch(q);
  };

  const buildLink = (r) => {
    if (r.kind === "lesson" && r.lesson_id) {
      return `/beit-midrash/lessons?open=${r.lesson_id}`;
    }
    if (!r?.book || !r.chapter) return null;
    const v = r.verse || 1;
    return `/reader/${bookUrl(r.book)}/${r.chapter}?v=${v}`;
  };

  // Highlight the search query inside Hebrew text. Match against nikud-stripped
  // form so users don't have to type vowels, but render the original (vocalised)
  // text with the matched substring boxed in gold.
  const highlight = (text) => {
    if (!text || !q) return text;
    const bareText = stripNikud(text);
    const bareQ = stripNikud(q.trim());
    if (!bareQ) return text;
    const idx = bareText.indexOf(bareQ);
    if (idx < 0) return text;
    // Walk the original text and figure out which slice corresponds to the
    // matched bareQ run (skipping nikud chars while counting).
    let bareCount = 0;
    let startOrig = -1;
    let endOrig = -1;
    for (let i = 0; i < text.length; i++) {
      const ch = text.charCodeAt(i);
      const isNikud = ch >= 0x0591 && ch <= 0x05c7;
      if (!isNikud) {
        if (bareCount === idx && startOrig === -1) startOrig = i;
        if (bareCount === idx + bareQ.length - 1) {
          endOrig = i + 1;
          break;
        }
        bareCount += 1;
      }
    }
    if (startOrig < 0) return text;
    if (endOrig < 0) endOrig = text.length;
    return (
      <>
        {text.substring(0, startOrig)}
        <span className="bg-gold/30 text-foreground px-1 rounded-sm">
          {text.substring(startOrig, endOrig)}
        </span>
        {text.substring(endOrig)}
      </>
    );
  };

  // Group results in display, but keep server-side ordering (tanakh → commentary → other).
  const grouped = (() => {
    const buckets = { tanakh: [], commentary: [], other: [] };
    results.forEach((r) => {
      const k = r.kind || "other";
      buckets[k] = buckets[k] || [];
      buckets[k].push(r);
    });
    return buckets;
  })();

  const renderCard = (r, idx) => {
    const link = buildLink(r);
    const isFuzzy = r.matched_via === "fuzzy" || r.matched_via === "normalized";
    return (
      <div
        key={`${r.ref}-${idx}`}
        data-testid="search-result"
        data-kind={r.kind}
        data-matched-via={r.matched_via || "exact"}
        className="spine-card rounded-sm p-5"
      >
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <span
            data-testid="search-result-title"
            className="font-heading text-base text-gold flex items-center gap-2"
            dir="rtl"
          >
            {r.kind === "tanakh" && r.book && r.chapter
              ? formatHebrewRef(r.book_he || r.book, r.chapter, r.verse)
              : r.kind === "commentary" && r.book && r.chapter
              ? `${r.commentator_he || r.commentator} · ${formatHebrewRef(r.book_he || r.book, r.chapter, r.verse)}`
              : r.title_he || r.ref}
            {isFuzzy && (
              <span
                data-testid="search-result-fuzzy-badge"
                className="font-latin not-italic px-1.5 py-0.5 text-[9px] tracking-widest uppercase rounded-sm bg-muted/30 text-muted-foreground border border-border/50"
              >
                {t("search.fuzzy_badge")}
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-heading tracking-widest uppercase text-muted-foreground">
              {KIND_LABEL[r.kind] || ""}
            </span>
            {link && (
              <Link
                to={link}
                data-testid="search-result-link"
                className="text-sm text-foreground hover:text-gold flex items-center gap-1 font-heading"
              >
                {t("search.open_result")}
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            )}
          </div>
        </div>
        {r.he && (
          <p
            dir="rtl"
            className="torah-verse hebrew-text text-xl text-foreground leading-loose text-right"
          >
            {highlight(r.he)}
          </p>
        )}
        {r.en && (
          <p className="font-latin italic text-sm text-muted-foreground mt-2 text-start">
            {r.en.slice(0, 200)}
            {r.en.length > 200 ? "..." : ""}
          </p>
        )}
        {/* Save to notebook — applicable for verses & commentaries */}
        {(r.kind === "tanakh" || r.kind === "commentary") && (
          <div className="mt-3 flex justify-end">
            <SaveToNotebookButton
              variant="chip"
              testIdSuffix={`search-${idx}`}
              payload={{
                source_type: r.kind === "commentary" ? "commentary" : "search",
                source_book: r.book,
                source_chapter: r.chapter,
                source_verse: r.verse,
                source_label: r.kind === "commentary" ? (r.commentator_he || r.commentator) : undefined,
                source_ref_he:
                  r.book && r.chapter
                    ? formatHebrewRef(r.book_he || r.book, r.chapter, r.verse)
                    : (r.title_he || r.ref),
                content_he: r.he || "",
                content_text: r.en || "",
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-12" data-testid="search-page" dir={dir}>
      <div className="text-center mb-10 space-y-3 animate-fade-in-up">
        <span className="font-latin italic text-sm tracking-widest uppercase text-gold">
          {t("search.page_eyebrow")}
        </span>
        <h1 className="font-heading text-4xl md:text-5xl text-foreground">
          {t("search.page_title")}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
          {t("search.page_subtitle")}
        </p>
        <div className="ornamental-divider mx-auto max-w-md">✦</div>
      </div>

      <form onSubmit={submit} className="flex gap-2 mb-10" data-testid="search-form">
        <div className="relative flex-1">
          <SearchIcon
            className="absolute top-1/2 -translate-y-1/2 end-3 h-4 w-4 text-muted-foreground"
            strokeWidth={1.5}
          />
          <Input
            data-testid="search-input"
            dir={dir}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.input_placeholder")}
            className="bg-card border-border/60 pr-3 pl-10 text-start h-12 text-base font-body focus-visible:ring-gold"
          />
        </div>
        <Button
          type="submit"
          data-testid="search-submit"
          disabled={!q.trim() || loading}
          className="sacred-btn h-12 px-6"
        >
          {t("search.submit")}
        </Button>
      </form>

      {loading && (
        <div className="space-y-3" data-testid="search-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-20 shimmer-bg rounded" />
          ))}
        </div>
      )}

      {didYouMean && (
        <div
          data-testid="search-did-you-mean"
          className="mb-4 rounded-sm border border-gold/40 bg-gold/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        >
          <p className="font-heading text-sm text-foreground">
            {t("search.did_you_mean")}{" "}
            <span className="text-gold" data-testid="search-dym-label">
              {didYouMean.label}
            </span>
            ?
          </p>
          {didYouMean.book && didYouMean.chapter && (
            <Link
              to={`/reader/${bookUrl(didYouMean.book)}/${didYouMean.chapter}${didYouMean.verse ? `?v=${didYouMean.verse}` : ""}`}
              data-testid="search-dym-open"
              className="font-heading text-xs text-foreground hover:text-gold inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-gold/30 hover:bg-gold/10 transition-colors"
            >
              {t("search.open_dym")}
              <ArrowLeft className="h-3 w-3" strokeWidth={1.5} />
            </Link>
          )}
        </div>
      )}

      {!loading && (results.length > 0 || personal.length > 0) && (
        <div className="space-y-6" data-testid="search-results">
          <p className="text-sm text-muted-foreground">
            {t("search.results_found", { count: results.length + personal.length })}{" "}
            <span className="text-gold">{t("search.results_tanakh_count", { count: groups.tanakh })}</span>
            {groups.commentary > 0 && (
              <>, <span className="text-foreground">{t("search.results_commentary_count", { count: groups.commentary })}</span></>
            )}
            {groups.other > 0 && (
              <>, <span className="text-muted-foreground">{t("search.results_other_count", { count: groups.other })}</span></>
            )}
            {personal.length > 0 && (
              <>, <span className="text-gold">{t("search.results_personal_count", { count: personal.length })}</span></>
            )}
          </p>

          {/* Personal — show FIRST when user has matches in their own corpus */}
          {personal.length > 0 && (
            <section data-testid="search-group-personal" className="space-y-3">
              <h2 className="font-heading text-xl text-foreground flex items-center gap-2">
                <BookMarked className="h-4 w-4 text-gold" strokeWidth={1.5} />
                {t("search.group_personal")}
                <span className="text-xs text-muted-foreground">({personal.length})</span>
              </h2>
              <div className="space-y-3">
                {personal.map((r, i) => renderCard(r, `p-${i}`))}
              </div>
            </section>
          )}

          {/* Tanakh first — always */}
          {grouped.tanakh.length > 0 && (
            <section data-testid="search-group-tanakh" className="space-y-3">
              <h2 className="font-heading text-xl text-foreground flex items-center gap-2">
                <span className="text-gold">●</span>
                {t("search.group_tanakh")}
                <span className="text-xs text-muted-foreground">({grouped.tanakh.length})</span>
              </h2>
              <div className="space-y-3">
                {grouped.tanakh.map((r, i) => renderCard(r, i))}
              </div>
            </section>
          )}

          {grouped.commentary.length > 0 && (
            <section data-testid="search-group-commentary" className="space-y-3">
              <h2 className="font-heading text-xl text-foreground flex items-center gap-2">
                <span className="text-gold/70">●</span>
                {t("search.group_commentary")}
                <span className="text-xs text-muted-foreground">({grouped.commentary.length})</span>
              </h2>
              <div className="space-y-3">
                {grouped.commentary.map((r, i) => renderCard(r, i))}
              </div>
            </section>
          )}

          {grouped.other.length > 0 && (
            <section data-testid="search-group-other" className="space-y-3">
              <h2 className="font-heading text-xl text-muted-foreground flex items-center gap-2">
                <span className="text-muted-foreground">○</span>
                {t("search.group_other")}
                <span className="text-xs text-muted-foreground">({grouped.other.length})</span>
              </h2>
              <div className="space-y-3">
                {grouped.other.map((r, i) => renderCard(r, i))}
              </div>
            </section>
          )}
        </div>
      )}

      {!loading && results.length === 0 && personal.length === 0 && initialQ && (
        <div
          className="text-center py-12 text-muted-foreground space-y-3"
          data-testid="search-empty"
        >
          <p className="font-heading text-lg">{t("search.empty_title")}</p>
          <ul className="text-sm space-y-1">
            <li>{t("search.empty_tip_1")}</li>
            <li>{t("search.empty_tip_2")}</li>
            <li>{t("search.empty_tip_3")}</li>
          </ul>
        </div>
      )}

      {!loading && !initialQ && (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">
            {t("search.examples_prefix")} <span className="text-foreground">{t("search.example_1")}</span> ·{" "}
            <span className="text-foreground">{t("search.example_2")}</span> ·{" "}
            <span className="text-foreground">{t("search.example_3")}</span>
          </p>
        </div>
      )}
    </div>
  );
}
